import { useContext, useEffect, useMemo, useState } from "react";

import { IPharmacyInfo } from "@/Common/Api/Pharmacy/Dto/Responses/IPharmacyInfo";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { usePartialState } from "@/Common/CustomHooks/usePartialState";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { DirectoryEntityType } from "@/ContactDirectory/Api/medfar-contactdirectory/dist";
import { PhoneNumberHelper } from "@/ContactDirectory/Helpers/PhoneNumberHelper";
import { DirectoryPharmacy } from "@/ContactDirectory/Models//DirectoryPharmacy";
import { PharmacyProxy } from "@/ContactDirectory/Models/PharmacyProxy";
import { useDefaultDirectoryCountryAndState } from "../Hooks/useDefaultDirectoryCountryAndState";
import { useDefaultFaxDirectoryContacts } from "../Hooks/useDefaultFaxDirectoryContacts";
import { DirectoryFaxUpdateOptions } from "../Models/DirectoryFaxUpdateOptions";
import { IPatientPreferredDirectoryContactsContext, PatientPreferredDirectoryContactsContext } from "../Models/PatientPreferredDirectoryContactsContext";
import { FaxDirectoryPharmacistManualEntry } from "./Common/FaxDirectoryPharmacistManualEntry";
import { FaxDirectorySearchComponent } from "./Search/FaxDirectorySearchComponent";
import { FaxDirectorySearchResultItem } from "./Search/FaxDirectorySearchResultItem";
import { FaxDirectoryManualEntry } from "./Selection/FaxDirectoryManualEntry";
import { FaxDirectorySelectedEntityComponent } from "./Selection/FaxDirectorySelectedEntityComponent";
import { DirectoryPopupController } from "./DirectoryPopupController";

import styles from "./FaxDirectoryForm.module.scss";

interface IFaxDirectoryPharmacySelectorComponentProps {
    patientContactId: string;
    isFaxValid: (isValid: boolean) => void;
    onFaxUpdate: (faxUpdateOptions: DirectoryFaxUpdateOptions & { pharmacy: IPharmacyInfo }) => void
    customPharmacy?: IPharmacyInfo;
}

interface ISelectedFaxInformation {
    pharmacy: PharmacyProxy | null;
    selectedFaxNumberId: string | null;
    selectedFaxNumber: string;
    countryId: string;
    stateId: string;
}

interface IManualFaxInformation {
    banner: string;
    faxNumber: string;
    countryId: string;
    provinceId: string;
    pharmacists: string;
}

function FaxDirectoryPharmacySelectorComponent(props: IFaxDirectoryPharmacySelectorComponentProps): JSX.Element {
    const { preferredPharmacy, externalConsultants } = useDefaultFaxDirectoryContacts(props.patientContactId);
    const context: IPatientPreferredDirectoryContactsContext = {
        patientContactId: props.patientContactId,
        preferredPharmacy,
        externalConsultants
    };
    return (
        <PatientPreferredDirectoryContactsContext.Provider value={context}>
            <FaxDirectoryPharmacySelectorComponentWithoutContext {...props} />
        </PatientPreferredDirectoryContactsContext.Provider>
    );
}

function getFaxInfo(item: Partial<FaxDirectorySearchResultItem>, countryId: string, stateId: string): ISelectedFaxInformation {
    const directoryPharmacy = (item.preferredPharmacy?.getDirectoryPharmacy() || item.entity) as DirectoryPharmacy;
    const fax = directoryPharmacy?.faxNumbers?.firstOrDefault();

    let faxInfo = {
        selectedEntity: directoryPharmacy,
        selectedFaxNumberId: fax?.id,
        pharmacy: item.preferredPharmacy ?? new PharmacyProxy({ directoryPharmacy: directoryPharmacy }),
        countryId: fax?.countryId,
        stateId: directoryPharmacy?.address?.stateId,
        selectedFaxNumber: PhoneNumberHelper.validate(fax?.number, fax?.countryId) ? fax?.number : ""
    };

    if (!directoryPharmacy) { // custom pharmacy
        const faxNumber = item.preferredPharmacy?.CustomPharmacy?.Fax;

        faxInfo = {
            ...faxInfo,
            countryId,
            stateId,
            selectedFaxNumber: faxNumber,
            selectedFaxNumberId: "custom_pharmacy_faxNumber"
        };
    }

    return faxInfo;
}

function FaxDirectoryPharmacySelectorComponentWithoutContext(props: IFaxDirectoryPharmacySelectorComponentProps): JSX.Element {
    const { getText } = useTranslation();
    const { defaultCountry, defaultState } = useDefaultDirectoryCountryAndState(props.patientContactId, true);

    const { preferredPharmacy } = useContext(PatientPreferredDirectoryContactsContext);

    const [showFaxDirectorySelector, setShowFaxDirectorySelector] = useState<boolean>(false);

    const getSelectedPharmacyInfo = (): IPharmacyInfo => {
        const selectedPharmacy = selectedFaxInfo.pharmacy;

        if (selectedPharmacy) {
            const pharmacy = selectedPharmacy?.getPharmacyInfo(false) ?? {} as IPharmacyInfo;

            if (IsNullOrWhiteSpace(pharmacy.Banner) && !IsNullOrWhiteSpace(manualState.banner))
                pharmacy.Banner = manualState.banner;

            if (!IsNullOrWhiteSpace(manualState.pharmacists))
                pharmacy.Name = manualState.pharmacists;

            if (!IsNullOrWhiteSpace(manualState.faxNumber)) {
                pharmacy.Fax = manualState.faxNumber;
            }
            else if (!PhoneNumberHelper.validate(pharmacy.Fax, selectedFaxInfo.countryId)) {
                pharmacy.Fax = "";
            }

            return pharmacy;
        }
        else if (isManualFaxValid || isPreferredChecked)
            return {
                Name: manualState.pharmacists,
                Phone: "",
                Address: "",
                Fax: manualState.faxNumber,
                Banner: manualState.banner
            };
        else
            return null;
    };

    const [selectedFaxInfo, setSelectedFaxInfo] = usePartialState<ISelectedFaxInformation>(getFaxInfo({ preferredPharmacy }, defaultCountry.Id, defaultState.Id));

    useEffect(() => {
        if (preferredPharmacy && (!props.customPharmacy?.Fax || props.customPharmacy?.Fax === preferredPharmacy.CustomPharmacy.Fax)) {
            setSelectedFaxInfo(getFaxInfo({ preferredPharmacy }, defaultCountry.Id, defaultState.Id));
        }
    }, [preferredPharmacy, props.customPharmacy]);

    const [manualState, setManualState] = useState<IManualFaxInformation>(
        (!!props.customPharmacy?.Fax) ?
            {
                banner: props.customPharmacy.Banner,
                countryId: defaultCountry.Id,
                provinceId: defaultState.Id,
                pharmacists: props.customPharmacy.Name,
                faxNumber: props.customPharmacy.Fax
            }
            : {
                banner: "",
                countryId: defaultCountry.Id,
                provinceId: defaultState.Id,
                pharmacists: "",
                faxNumber: ""
            }
    );

    useEffect(() => {
        if (!!props.customPharmacy?.Fax) {
            setManualState({ ...manualState, faxNumber: props.customPharmacy.Fax, banner: props.customPharmacy.Banner });
        }
    }, [props.customPharmacy]);

    const [isManualFaxValid, setIsManualFaxValid] = useState<boolean>(false);
    const [isPreferredChecked, setIsPreferredChecked] = useState<boolean>(!preferredPharmacy);

    const showManualPharmacistsEntry = useMemo(
        () => IsNullOrWhiteSpace(selectedFaxInfo?.pharmacy?.getPharmacyInfo()?.Name),
        [selectedFaxInfo.pharmacy]
    );

    const isPharmacistsValid: boolean = useMemo(
        // we need a pharmacist if the receiver is in Quebec
        () => {
            const selectedPharmacyInfo = getSelectedPharmacyInfo();
            if (showManualPharmacistsEntry) {
                return cacheStore.GetStateMedfarDictionariesById_MedfarDic(selectedFaxInfo.stateId ?? manualState.provinceId)?.SubdivisionCode !== "CA-QC"
                    || !IsNullOrWhiteSpace(manualState.pharmacists);
            }
            else {
                return cacheStore.GetStateMedfarDictionariesById_MedfarDic(selectedFaxInfo.stateId)?.SubdivisionCode !== "CA-QC"
                    || !IsNullOrWhiteSpace(selectedPharmacyInfo.Name);
            }
        },
        [manualState.pharmacists, manualState.provinceId, selectedFaxInfo?.pharmacy, selectedFaxInfo?.stateId]
    );

    const selectedEntityHasFaxInfo = useMemo(
        () => selectedFaxInfo?.pharmacy?.toDirectorySelectableEntity()?.faxInfos?.any() ?? false,
        [selectedFaxInfo.pharmacy]
    );

    const showManualFaxNumberEntry = useMemo(
        () => !selectedFaxInfo.pharmacy
            || !selectedEntityHasFaxInfo
            || IsNullOrWhiteSpace(selectedFaxInfo.selectedFaxNumber),
        [selectedFaxInfo.pharmacy, selectedEntityHasFaxInfo, selectedFaxInfo.selectedFaxNumber]
    );

    const isValid = useMemo(
        () => {
            const isFinalFaxNumberValid = showManualFaxNumberEntry
                ? isManualFaxValid
                : PhoneNumberHelper.validate(
                    selectedFaxInfo.selectedFaxNumber,
                    selectedFaxInfo.countryId,
                    selectedFaxInfo.stateId
                );
            return isFinalFaxNumberValid && isPharmacistsValid;
        },
        [
            isPharmacistsValid,
            selectedFaxInfo.selectedFaxNumber,
            selectedFaxInfo.countryId,
            selectedFaxInfo.stateId,
            isManualFaxValid,
            showManualFaxNumberEntry
        ]
    );

    useEffect(() => props.isFaxValid(isValid), [isValid]);

    useEffect(
        () => {
            const selectedPharmacyInfo = getSelectedPharmacyInfo();

            if (selectedPharmacyInfo) {
                const countryId = showManualFaxNumberEntry ? manualState.countryId : selectedFaxInfo.countryId;
                const stateId = showManualFaxNumberEntry ? manualState.provinceId : selectedFaxInfo.stateId;

                const countryCode = cacheStore.CountryCache.firstOrDefault(p => p.Id === countryId)?.Alpha2Code;
                const stateCode = cacheStore.GetStateMedfarDictionariesById_MedfarDic(stateId)?.SubdivisionCode;

                const isSelectedPharmacyValid = !IsNullOrWhiteSpace(selectedPharmacyInfo?.Name)
                    && (showManualFaxNumberEntry
                        ? isManualFaxValid
                        : PhoneNumberHelper.validate(selectedFaxInfo.selectedFaxNumber, countryId, stateId)
                    );

                props.onFaxUpdate({
                    pharmacy: selectedPharmacyInfo,
                    shouldUpdatePatientLink: isPreferredChecked,
                    countryCode: countryCode,
                    stateCode: stateCode,
                    selectedEntity: isSelectedPharmacyValid ? selectedFaxInfo.pharmacy?.getDirectoryPharmacy() : null,
                    selectedFaxNumberId: selectedFaxInfo.selectedFaxNumberId,
                    faxNumber: selectedFaxInfo.selectedFaxNumber,
                    entityType: DirectoryEntityType.Pharmacy
                });
            }
        },
        [
            isPreferredChecked,
            selectedFaxInfo.pharmacy,
            selectedFaxInfo.selectedFaxNumberId,
            selectedFaxInfo.selectedFaxNumber,
            selectedFaxInfo.countryId,
            selectedFaxInfo.stateId,
            manualState.banner,
            manualState.countryId,
            manualState.provinceId,
            manualState.pharmacists,
            isManualFaxValid,
            showManualFaxNumberEntry
        ]
    );

    const clearManualState = (): void => {
        setManualState({
            banner: "",
            countryId: defaultCountry.Id,
            provinceId: defaultState.Id,
            pharmacists: "",
            faxNumber: ""
        });
        setIsPreferredChecked(!preferredPharmacy);
    };

    const closeSelectedEntity = (): void => {
        setSelectedFaxInfo({ pharmacy: null, stateId: null, countryId: null });
        clearManualState();
    };

    const isInfoEqualToPreferredPharmacy = (): boolean => {
        const preferredPharmacyInfo = preferredPharmacy?.getPharmacyInfo();

        if (!preferredPharmacyInfo)
            return false;

        const selectedPharmacyInfo = getSelectedPharmacyInfo();

        if (selectedFaxInfo.pharmacy) {
            return selectedFaxInfo.pharmacy.DirectoryPharmacyId
                ? selectedFaxInfo.pharmacy.DirectoryPharmacyId === preferredPharmacy.DirectoryPharmacyId
                : selectedPharmacyInfo.Banner === preferredPharmacyInfo.Banner
                    && selectedPharmacyInfo.Name === preferredPharmacyInfo.Name
                    && selectedPharmacyInfo.Fax === preferredPharmacyInfo.Fax;
        }
        else { // manual entry
            return manualState.banner.trim() === preferredPharmacyInfo.Banner
                && manualState.pharmacists.trim() === preferredPharmacyInfo.Name
                && manualState.faxNumber.trim() === preferredPharmacyInfo.Fax;
        }
    };

    return (
        <div>
            <div>
                <a
                    className={styles.addToDirectoryLink}
                    onClick={() => setShowFaxDirectorySelector(true)}
                >
                    {getText("AddToDirectoryLabel")}
                </a>
                {showFaxDirectorySelector && (
                    <DirectoryPopupController
                        onSave={(entity: DirectoryPharmacy) => {
                            const fax = entity.faxNumbers.firstOrDefault();
                            setSelectedFaxInfo({
                                pharmacy: new PharmacyProxy({ directoryPharmacy: entity }),
                                stateId: entity.address.stateId,
                                selectedFaxNumberId: fax?.id,
                                selectedFaxNumber: fax?.number,
                                countryId: fax?.countryId
                            });
                            clearManualState();
                            setShowFaxDirectorySelector(false);
                        }}
                        onClose={() => setShowFaxDirectorySelector(false)}
                        onEntityUseInstead={(entity: DirectoryPharmacy) => {
                            const fax = entity.faxNumbers.firstOrDefault();
                            setSelectedFaxInfo({
                                pharmacy: new PharmacyProxy({ directoryPharmacy: entity }),
                                stateId: entity.address?.stateId,
                                selectedFaxNumberId: fax?.id,
                                selectedFaxNumber: fax?.number,
                                countryId: fax?.countryId
                            });
                            clearManualState();
                            setShowFaxDirectorySelector(false);
                        }}
                        onModify={(entity: DirectoryPharmacy) => {
                            const fax = entity.faxNumbers.firstOrDefault();
                            setSelectedFaxInfo({
                                pharmacy: new PharmacyProxy({ directoryPharmacy: entity }),
                                stateId: entity.address.stateId,
                                selectedFaxNumberId: fax?.id,
                                selectedFaxNumber: fax?.number,
                                countryId: fax?.countryId
                            });
                            clearManualState();
                            setShowFaxDirectorySelector(false);
                        }}
                        defaultType={DirectoryEntityType.Pharmacy}
                    />
                )}
            </div>
            <div>
                <label className="bold">{getText("PharmacyNameLabel")}</label>
                <FaxDirectorySearchComponent
                    showPreferredPharmacy
                    patientContactId={props.patientContactId}
                    onItemSelected={(selection: FaxDirectorySearchResultItem) => {
                        setSelectedFaxInfo(getFaxInfo(selection, defaultCountry.Id, defaultState.Id));
                        clearManualState();
                    }}
                    onSearchTextChanged={banner => {
                        setManualState({ ...manualState, banner });
                    }}
                    entityTypes={[DirectoryEntityType.Pharmacy]}
                    dataCy="contactDirectory-pharmacistSearch"
                    allowExpandingGeographicSearch
                />
            </div>
            {selectedFaxInfo.pharmacy && (
                <div
                    className={styles.selectedEntity}
                    data-cy="fax-directory-pharmacy-selector-selected-entity"
                >
                    <FaxDirectorySelectedEntityComponent
                        entity={selectedFaxInfo.pharmacy.toDirectorySelectableEntity()}
                        selectedNumberId={selectedFaxInfo.selectedFaxNumberId}
                        setSelectedNumberId={id => {
                            if (id === selectedFaxInfo.selectedFaxNumberId)
                                return;

                            const fax = selectedFaxInfo.pharmacy.getDirectoryPharmacy()?.faxNumbers?.firstOrDefault(p => p.id === id);
                            setSelectedFaxInfo({
                                selectedFaxNumberId: id,
                                countryId: fax?.countryId,
                                selectedFaxNumber: fax?.number
                            });
                        }}
                        close={closeSelectedEntity}
                        patientContactId={props.patientContactId}
                    />
                </div>
            )}
            {showManualPharmacistsEntry && (
                <FaxDirectoryPharmacistManualEntry
                    pharmacist={manualState.pharmacists}
                    setPharmacists={pharmacists => {
                        setManualState({ ...manualState, pharmacists });
                    }}
                    isValid={isPharmacistsValid}
                />
            )}
            {showManualFaxNumberEntry && (
                <div>
                    <FaxDirectoryManualEntry
                        faxNumber={manualState?.faxNumber ?? ""}
                        setSelectedFaxNumber={(faxNumber, countryId, provinceId) => setManualState({ ...manualState, faxNumber, countryId, provinceId })}
                        setValid={isValid => setIsManualFaxValid(isValid)}
                        showProvinceField
                        patientContactId={props.patientContactId}
                        isForPharmacy
                    />
                </div>
            )}
            {!isInfoEqualToPreferredPharmacy() && (
                <CheckboxControl
                    labelClassName="margin-sm-top"
                    checked={isPreferredChecked}
                    onChange={() => setIsPreferredChecked(!isPreferredChecked)}
                    label={getText("SetPreferedPharmacyLabel")}
                    disabled={!isValid}
                />
            )}
        </div>
    );
}

export { FaxDirectoryPharmacySelectorComponent, IFaxDirectoryPharmacySelectorComponentProps };
